import { useState, useEffect } from "react";
import { TestResponse } from "@/types/test-response"; 
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TestIntegrityScore } from "../TestIntegrityScore";
import { ScoreActions } from "../ScoreActions";
import { QuestionResponseCard } from "../QuestionResponseCard";
import { MonitoringEvent } from "@/types/monitoring";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, Check } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { calculateTotalScore, calculateAbsoluteScore } from "@/utils/score-utils";
import { SkillScoreTable } from "../SkillScoreTable";
import { PercentileChart } from "../PercentileChart";

interface ResponsesTabProps {
  response: TestResponse;
  scores?: Record<string, number>;
  isEditing?: boolean;
  isLoading?: boolean;
  monitoringEvents?: MonitoringEvent[];
  onScoreChange?: (questionId: string, score: number) => void;
  setIsEditing?: (value: boolean) => void;
  handleConfirmScores?: () => void;
  handleAcceptAIScores?: () => void;
}

export function ResponsesTab({
  response,
  scores = {},
  isEditing = false,
  isLoading = false,
  monitoringEvents = [],
  onScoreChange,
  setIsEditing,
  handleConfirmScores,
  handleAcceptAIScores,
}: ResponsesTabProps) {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Calculate total possible score and initialize score states
  const totalPossibleScore = response.test.questions.reduce((acc, q) => acc + q.marks, 0);
  
  // Initialize local user score state from response data or calculated scores
  const [localUserScore, setLocalUserScore] = useState<number | null>(() => {
    // If scores are provided (editing mode), calculate from those
    if (scores && Object.keys(scores).length > 0) {
      return Object.values(scores).reduce((acc, score) => acc + score, 0);
    }
    // Otherwise calculate from response percentage
    return response.score !== null && response.score !== undefined
      ? calculateAbsoluteScore(response.score, totalPossibleScore)
      : null;
  });

  // Effect to update local score when scores are confirmed or edited
  useEffect(() => {
    if (scores && Object.keys(scores).length > 0) {
      // Directly calculate from provided scores
      const calculatedScore = Object.values(scores).reduce((acc, score) => acc + score, 0);
      setLocalUserScore(calculatedScore);
    } else if (response.score !== null && response.score !== undefined) {
      // Convert percentage back to absolute score
      setLocalUserScore(calculateAbsoluteScore(response.score, totalPossibleScore));
    }
  }, [scores, response.score, response.scores_confirmed, totalPossibleScore]);

  // Use stored AI score
  const totalAIScore = response.total_ai_score;
  
  const aiScorePercentage = Math.round((totalAIScore / totalPossibleScore) * 100) || 0;
  
  // Get question responses in correct order
  const sortedQuestions = [...response.test.questions].sort((a, b) => {
    return sortOrder === 'asc' ? a.order - b.order : b.order - a.order;
  });
  
  // Group monitoring events by question
  const monitoringEventsByQuestion: Record<string, MonitoringEvent[]> = {};
  monitoringEvents.forEach(event => {
    if (event.question_id) {
      if (!monitoringEventsByQuestion[event.question_id]) {
        monitoringEventsByQuestion[event.question_id] = [];
      }
      monitoringEventsByQuestion[event.question_id].push(event);
    }
  });

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Calculate user's score percentage if it exists
  const userScorePercentage = localUserScore !== null
    ? Math.round((localUserScore / totalPossibleScore) * 100) || 0
    : null;
  
  // Handler for accepting AI scores that updates local state immediately
  const handleAcceptAIScoresLocal = () => {
    setLocalUserScore(totalAIScore);
    handleAcceptAIScores && handleAcceptAIScores();
  };

  return (
    <div className="space-y-8">
      {/* Summary Card with Scores and Integrity - Equal size grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* AI Score Box */}
        <Card className="shadow-sm border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-muted/30 py-3 px-4">
            <CardTitle className="text-base text-primary">AI Score</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-col items-center justify-center py-2">
              <span className="text-3xl font-bold mb-2">{totalAIScore} / {totalPossibleScore}</span>
              <Progress 
                value={aiScorePercentage} 
                className="h-2 mb-2 w-full bg-slate-200 dark:bg-slate-700"
              />
              <span className="text-xs text-muted-foreground">Auto-generated</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Time to Complete */}
        <Card className="shadow-sm border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-muted/30 py-3 px-4">
            <CardTitle className="flex items-center justify-between text-base text-primary">
              <span>Time to Complete</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-col items-center justify-center py-2">
              {response.started_at && response.completed_at ? (
                (() => {
                  const totalMs = new Date(response.completed_at).getTime() - new Date(response.started_at).getTime();
                  const minutes = Math.floor(totalMs / 60000);
                  const seconds = Math.floor((totalMs % 60000) / 1000);
                  return (
                    <span className="text-3xl font-bold mb-2">
                      {minutes}m {seconds}s
                    </span>
                  );
                })()
              ) : (
                <>
                  <span className="text-3xl font-bold text-muted-foreground mb-1">—</span>
                  <span className="text-xs text-muted-foreground">In progress</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Integrity Score - Now in same grid column size */}
        <Card className="shadow-sm border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-muted/30 py-3 px-4">
            <CardTitle className="text-base text-primary">Integrity Score</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <TestIntegrityScore 
              testResponse={response} 
              isLoading={isLoading}
              className="h-full flex flex-col justify-center"
            />
          </CardContent>
        </Card>
      </div>

      {/* Analytics Section - Skills Table and Percentile Chart side by side */}
      {response.test.questions && response.test.questions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkillScoreTable
            questions={response.test.questions}
            candidateQuestionResponses={response.question_responses}
          />
          <PercentileChart response={response} />
        </div>
      )}

      {/* Question Responses List */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-primary">Question Responses</h3>
          <Button variant="ghost" size="sm" onClick={toggleSortOrder} className="text-muted-foreground flex items-center gap-1">
            <ArrowUpDown className="h-4 w-4" />
            {sortOrder === 'asc' ? 'Oldest First' : 'Latest First'}
          </Button>
        </div>
        
        <div className="space-y-6">
          {sortedQuestions.map((question, index) => {
            const questionResponse = response.question_responses.find(
              qr => qr.question_id === question.id
            );
            
            // Get monitoring events for this question
            const questionEvents = monitoringEventsByQuestion[question.id] || [];
            
            return (
              <QuestionResponseCard
                key={question.id}
                question={question}
                questionResponse={questionResponse}
                questionNumber={sortOrder === 'asc' ? index + 1 : sortedQuestions.length - index}
                isEditing={isEditing}
                score={scores?.[question.id] || 0}
                onScoreChange={(score) => onScoreChange && onScoreChange(question.id, score)}
                monitoringEvents={questionEvents}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
